// Spool card — ported from the production-schedule.html prototype.
// Self-contained: head row + pipeline pills + foot + optional stage
// details table. The card owns its own show-all state for the details
// table; expand state lives at the parent so multiple spools can be
// inspected at once without remounting state on re-sort.
import { useMemo, useState } from "react";
import type {
  SpoolStageDto,
  StageDefDto,
  StageKey,
  StageStatus,
  TeamMemberDto,
} from "../../../../../../shared/types/production-schedule";
import { useActiveStages } from "./StagesProvider";
import { addDays, daysBetween, fmtDate, isoToday } from "../lib/dates";
import {
  currentStage,
  denseStages,
  progressionStages,
  spoolHealth,
  stageDays,
  stageFinish,
  visibleStageRows,
  type SpoolHealth,
} from "../lib/stageHelpers";
import { SpoolStageDetailsTable } from "./SpoolStageDetailsTable";
import { SpoolComposition } from "./SpoolComposition";
import { SpoolPrintMenu } from "../modals/SpoolPrintMenu";
import { getDrawingPreviewUrl } from "../hooks/useProductionApi";
import type { SpoolDraft } from "./spoolDraft";

interface Props {
  spool: SpoolDraft;
  index: number;
  dragOver: boolean;
  focused?: boolean;
  cardRef?: (node: HTMLDivElement | null) => void;
  // Persisted stage rows for this spool. Empty before the job is
  // saved — pipeline + details panel render in read-only mode in
  // that case so the form still feels responsive.
  stages: SpoolStageDto[];
  team: TeamMemberDto[];
  weldBayKey: StageKey | null;
  // Default assignee per stage_key, fed by the Manage Team modal.
  // Synthetic stage rows pre-fill their Assigned to from this map.
  stageDefaultAssignees?: Map<string, number | null>;
  jobId?: number;
  jobNumber?: string;
  expanded: boolean;
  selected: boolean;
  // The drawing linked to this spool (workshop convention is 1:1).
  // When set, the per-spool "📐 Drawings" button opens its PDF
  // preview directly; when null the button is hidden.
  linkedDrawing?: { id: number; filename: string } | null;
  onChange(patch: Partial<SpoolDraft>): void;
  onDelete(): void;
  onDuplicate?(): void;
  onToggleExpand(): void;
  onToggleSelect(checked: boolean): void;
  onDragStart(): void;
  onDragOver(): void;
  onDragLeave(): void;
  onDrop(): void;
  // Persists a stage patch to the server (and refetches). One async
  // callback so SpoolCard never imports useProductionApi directly —
  // keeps the component testable with a mock.
  // Called after the composition form saves — used by SpoolTable to
  // refetch the job (the server has already recomputed stage hours
  // via the extrapolator). Wired through onStageChanged at the parent.
  onCompositionSaved?: () => void | Promise<void>;
  onStagePatch?: (
    stageKey: StageKey,
    patch: {
      hours?: number;
      assigneeId?: number | null;
      startDate?: string | null;
      doneDate?: string | null;
      status?: StageStatus;
    },
  ) => Promise<void>;
  // Cross-nav: switch the modal to the Drawings tab. Workshop uses
  // this to jump from a spool to its linked drawings without
  // hunting through the tab strip.
  onOpenDrawings?: () => void;
}

const HEALTH_CLASS: Record<SpoolHealth, string> = {
  overdue: "is-overdue",
  "at-risk": "is-at-risk",
  "on-track": "is-on-track",
  complete: "is-complete",
};

export function SpoolCard({
  spool,
  index,
  dragOver,
  focused,
  cardRef,
  stages,
  team,
  weldBayKey,
  stageDefaultAssignees,
  jobId,
  jobNumber,
  expanded,
  selected,
  linkedDrawing,
  onChange,
  onDelete,
  onDuplicate,
  onToggleExpand,
  onToggleSelect,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onStagePatch,
  onCompositionSaved,
  onOpenDrawings,
}: Props) {
  const [showAll, setShowAll] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const catalogue = useActiveStages();
  const stageByKey = useMemo(() => {
    const m = new Map<string, StageDefDto>();
    for (const s of catalogue) m.set(s.stageKey, s);
    return m;
  }, [catalogue]);

  const today = isoToday();
  const canPersist = typeof jobId === "number" && typeof spool.id === "number";
  const canBulk = typeof jobId === "number" && typeof jobNumber === "string";
  // Overlay the runtime stage catalogue on top of whatever's
  // persisted so a brand-new spool renders pills + a stage table
  // the user can edit. Edits upsert server-side via patchSpoolStage.
  const dense = useMemo(
    () => denseStages(stages, weldBayKey, stageDefaultAssignees, catalogue),
    [stages, weldBayKey, stageDefaultAssignees, catalogue],
  );
  // Single progression list drives BOTH the click handlers and the
  // completion math below. Without aligning these, a legacy spool
  // with a non-chosen weld bay still persisted as 'pending' would
  // (a) be skipped by Progress (correct), but (b) keep allComplete
  // = false and the percentage < 100 forever (wrong) — leaving the
  // user with an apparently incomplete spool the Progress button
  // can't finish. The stage details table (SpoolStageDetailsTable
  // below) still receives the full dense list because the workshop
  // wants to see the orphan weld-bay row in order to clear it.
  const active = useMemo(
    () => progressionStages(dense, weldBayKey),
    [dense, weldBayKey],
  );
  const cur = currentStage(active);
  const curDef = cur ? stageByKey.get(cur.stageKey) : null;
  const completeCount = active.filter((s) => s.status === "complete").length;
  const pct = active.length > 0 ? Math.round((completeCount / active.length) * 100) : 0;
  const allComplete = active.length > 0 && completeCount === active.length;
  const health = spoolHealth({ dueDate: spool.dueDate ?? null, stages: active, todayIso: today });
  const dueCls = useMemo(() => {
    if (!spool.dueDate) return "";
    if (spool.dueDate < today) return "is-overdue";
    const days = daysBetween(today, spool.dueDate);
    if (days <= 7) return "is-at-risk";
    return "";
  }, [spool.dueDate, today]);

  // Pipeline strip cells: every visible stage at flex:1 width.
  const pipelineRows = visibleStageRows(dense, weldBayKey, false, catalogue);

  const patch = async (
    stageKey: StageKey,
    p: Parameters<NonNullable<Props["onStagePatch"]>>[1],
  ) => {
    if (!onStagePatch) return;
    setBusy(true);
    try {
      await onStagePatch(stageKey, p);
    } catch (e) {
      // Without this surface the patch hits a server 500 (e.g. an
      // unmigrated production_stage_configs table) and the click
      // looks like a no-op. Show the user what went wrong so we
      // get diagnostic info instead of "the eye toggle isn't doing
      // anything" reports.
      window.alert(`Could not update ${stageKey}: ${e instanceof Error ? e.message : String(e)}`);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  // Advance the spool to a target stage: every earlier stage in the
  // progression list becomes 'complete' (with a doneDate seeded to
  // today / startDate filled in if missing), and the target itself
  // becomes 'in-progress'. The progression list excludes 'skipped'
  // rows + every weld bay except the one chosen for this spool, so
  // jumping forward skips past inactive bays automatically.
  //
  // No-ops:
  //   * target.status === "in-progress" → already current, nothing to do.
  //   * target.status === "complete" → re-opening a closed stage
  //     would leave later 'complete' stages stranded; we keep that
  //     correction in the stage details table where the workshop
  //     can also tidy up the trailing stages.
  const advanceTo = async (target: SpoolStageDto) => {
    if (target.status === "in-progress" || target.status === "complete") return;
    if (target.status === "skipped") return;
    const list = progressionStages(dense, weldBayKey);
    const targetIdx = list.findIndex((s) => s.stageKey === target.stageKey);
    if (targetIdx < 0) return;
    for (let i = 0; i < targetIdx; i++) {
      const s = list[i]!;
      if (s.status === "complete") continue;
      await patch(s.stageKey as StageKey, {
        status: "complete",
        // Seed startDate too — a stage that jumps straight from
        // 'pending' to 'complete' would otherwise leave the table
        // showing a finish without a start.
        startDate: s.startDate ?? today,
        doneDate: s.doneDate ?? today,
      });
    }
    await patch(target.stageKey as StageKey, {
      status: "in-progress",
      startDate: target.startDate ?? today,
    });
  };

  // Progress button: advance to the next non-complete stage in the
  // progression list. When the current stage is 'in-progress',
  // advanceTo on the next stage marks the current complete as the
  // first step of the loop. When the current stage is the last
  // one, mark it complete in place.
  const onProgress = async () => {
    if (allComplete) return;
    const list = progressionStages(dense, weldBayKey);
    if (list.length === 0) return;
    const cur = list.find((s) => s.status === "in-progress");
    if (cur) {
      const idx = list.indexOf(cur);
      const next = list.slice(idx + 1).find((s) => s.status !== "complete");
      if (next) {
        await advanceTo(next);
      } else {
        await patch(cur.stageKey as StageKey, {
          status: "complete",
          doneDate: cur.doneDate ?? today,
        });
      }
      return;
    }
    const firstPending = list.find((s) => s.status === "pending");
    if (firstPending) await advanceTo(firstPending);
  };

  // Pill click: advance to that stage. Earlier active stages flip to
  // complete; later ones are left alone. Skipped pills are inert.
  const onPillClick = async (st: SpoolStageDto) => {
    await advanceTo(st);
  };

  // Cascade dates across active stages: each stage starts when the
  // previous one finishes, anchored to today (or the spool's
  // earliest existing start date if any).
  const onAutoSchedule = async () => {
    const startAnchor =
      active.map((s) => s.startDate).filter((d): d is string => Boolean(d)).sort()[0] ??
      today;
    let cursor = startAnchor;
    let scheduled = false;
    for (const def of catalogue) {
      const st = dense.find((x) => x.stageKey === def.stageKey);
      if (!st || st.status === "skipped") continue;
      await patch(def.stageKey as StageKey, { startDate: cursor });
      cursor = addDays(cursor, stageDays(st, catalogue));
      scheduled = true;
    }
    // After the loop, cursor sits one day past the last scheduled
    // finish — that's the spool's earliest realistic completion
    // date, suggested as the new due date.
    if (scheduled) onChange({ dueDate: cursor });
  };

  return (
    <div
      ref={cardRef}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(index));
        onDragStart();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragOver();
      }}
      onDragLeave={() => onDragLeave()}
      onDrop={(e) => {
        e.preventDefault();
        onDrop();
      }}
      className={[
        "spool-card",
        HEALTH_CLASS[health],
        dragOver ? "drag-over" : "",
        selected ? "is-selected" : "",
        focused ? "is-focused" : "",
      ].filter(Boolean).join(" ")}
    >
      <div className="spool-card-head">
        <span className="spool-drag" title="Drag to reorder">⠿</span>
        {canBulk ? (
          <input
            type="checkbox"
            className="spool-select"
            checked={selected}
            onChange={(e) => onToggleSelect(e.target.checked)}
            disabled={!canPersist}
            title={canPersist ? "Select for bulk print" : "Save the job to enable bulk print"}
          />
        ) : null}
        <button
          type="button"
          className="toggle-btn"
          onClick={onToggleExpand}
          title={expanded ? "Collapse stages" : "Expand stages"}
        >
          {expanded ? "−" : "+"}
        </button>
        <div className="spool-id-block">
          <input
            className="spool-id-num"
            value={spool.num}
            placeholder="SP-001"
            onChange={(e) => onChange({ num: e.target.value })}
          />
          <input
            className="spool-id-desc"
            value={spool.description ?? ""}
            placeholder="Spool description…"
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </div>
        <div className="spool-card-meta">
          <label className={`spool-due-pill ${dueCls}`} title="Spool due date">
            <span className="lbl">Due</span>
            <input
              type="date"
              value={spool.dueDate ?? ""}
              onChange={(e) => onChange({ dueDate: e.target.value || null })}
            />
          </label>
          <span className="spool-stat-pill" title="Estimated total workshop hours">
            <strong>{Number(spool.hours ?? 0).toFixed(1)}</strong>h
          </span>
          <span
            className="spool-stat-pill"
            title={`${completeCount} of ${active.length} active stages complete`}
          >
            <strong>{completeCount}/{active.length}</strong>
          </span>
          <span
            className="spool-stat-pill"
            title={cur ? `Current stage · ${curDef?.name ?? cur.stageKey}` : "All stages complete"}
          >
            <span
              className="dot"
              style={{ background: cur ? stageByKey.get(cur.stageKey)?.color ?? "#9CA09F" : "#2E7D32" }}
            />
            {cur ? curDef?.name ?? cur.stageKey : "Done"}
          </span>
        </div>
        {canPersist && linkedDrawing && typeof jobId === "number" && (
          <button
            type="button"
            className="btn-sm"
            onClick={() => void openLinkedDrawing(jobId, linkedDrawing.id, onOpenDrawings)}
            title={`Open ${linkedDrawing.filename} in a new tab`}
            aria-label="Open linked drawing"
          >
            📐 Drawing
          </button>
        )}
        {canBulk && canPersist ? (
          <SpoolPrintMenu
            jobId={jobId as number}
            spoolId={spool.id as number}
            spoolNum={spool.num || `spool-${spool.id}`}
          />
        ) : canBulk ? (
          <button className="btn-sm" disabled title="Save the job to enable per-spool printing">🖨 ▾</button>
        ) : null}
        <button
          type="button"
          className="btn-sm progress-btn"
          onClick={() => void onProgress()}
          disabled={!canPersist || allComplete || busy}
          title={allComplete
            ? "All active stages complete"
            : "Advance current stage: pending → in-progress → complete → next stage"}
        >
          {allComplete ? "✓ Done" : "Progress ▶"}
        </button>
        <button
          type="button"
          className="btn-sm"
          onClick={() => void onAutoSchedule()}
          disabled={!canPersist || busy}
          title="Cascade dates: each stage starts when the previous finishes"
        >
          ⟳
        </button>
        {onDuplicate ? (
          <button
            type="button"
            className="btn-sm"
            onClick={onDuplicate}
            title="Duplicate this spool"
            aria-label="Duplicate spool"
          >
            ⎘
          </button>
        ) : null}
        <button
          type="button"
          className="btn-sm danger"
          onClick={onDelete}
          aria-label="Delete spool"
          title="Delete spool"
        >
          ×
        </button>
      </div>
      {typeof jobId === "number" && typeof spool.id === "number" ? (
        <div style={{ padding: "4px 12px 0 12px" }}>
          <button
            type="button"
            className="btn-sm"
            onClick={() => setComposeOpen((v) => !v)}
            title="Pipe size, per-piece counts, weld joints — drives stage hours"
            aria-expanded={composeOpen}
            style={{ marginBottom: 4 }}
          >
            {composeOpen ? "▼" : "▶"} Composition
          </button>
          {composeOpen ? (
            <SpoolComposition
              jobId={jobId}
              spoolId={spool.id}
              onAfterSave={onCompositionSaved}
            />
          ) : null}
        </div>
      ) : null}
      <div className="pipeline-strip">
        {pipelineRows.length === 0 ? (
          <div style={{
            flex: 1,
            fontSize: 10,
            color: "var(--bsp-fg-muted)",
            fontStyle: "italic",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 6,
          }}>
            {canPersist
              ? "No active stages — toggle the eye in the stage table to show all."
              : "Save the job to populate the stage pipeline."}
          </div>
        ) : (
          pipelineRows.map((st) => {
            const def = stageByKey.get(st.stageKey);
            if (!def) return null;
            const isCur = cur?.stageKey === st.stageKey && st.status !== "complete";
            const cls = [
              "pipeline-cell",
              `is-${st.status}`,
              isCur ? "is-current" : "",
            ].filter(Boolean).join(" ");
            const tip = [
              def.name,
              st.hours ? `${st.hours}h` : null,
              st.startDate ? `· ${fmtDate(st.startDate)}` : null,
              `→ ${st.status}`,
              fmtDate(stageFinish(st)) !== "—" ? `finish ${fmtDate(stageFinish(st))}` : null,
            ].filter(Boolean).join(" · ");
            return (
              <button
                key={st.stageKey}
                type="button"
                className={cls}
                style={{ background: def.color }}
                title={tip}
                onClick={() => void onPillClick(st)}
                disabled={!canPersist || busy}
              >
                {def.short}
              </button>
            );
          })
        )}
      </div>
      <div className="pipeline-foot">
        <div className="pipeline-foot-left">
          {cur && !allComplete ? (
            <>
              <span
                className="dot"
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: stageByKey.get(cur.stageKey)?.color ?? "#9CA09F",
                }}
              />
              <span>
                {curDef?.name ?? cur.stageKey}
                {cur.startDate ? ` · started ${fmtDate(cur.startDate)}` : ""}
              </span>
            </>
          ) : allComplete ? (
            <span>✓ All active stages complete</span>
          ) : (
            <span style={{ color: "var(--bsp-fg-muted)", fontWeight: 500 }}>No active stages</span>
          )}
        </div>
        <div className="pipeline-progress">
          <div className="pipeline-progress-bar" style={{ width: `${pct}%` }} />
        </div>
        <span style={{ minWidth: 34, textAlign: "right", fontFamily: "var(--bsp-font-mono)", fontWeight: 700 }}>{pct}%</span>
      </div>
      {expanded ? (
        <SpoolStageDetailsTable
          stages={dense}
          team={team}
          weldBayKey={weldBayKey}
          showAll={showAll}
          onToggleShowAll={() => setShowAll((v) => !v)}
          onStagePatch={(k, p) => patch(k, p)}
        />
      ) : null}
    </div>
  );
}

// Mint a SAS URL and open the PDF in a new tab. Falls back to the
// Drawings-tab jump if the URL mint fails (network blip, expired
// auth) so the user still has a path to the file.
async function openLinkedDrawing(
  jobId: number,
  drawingId: number,
  onFallback?: () => void,
): Promise<void> {
  try {
    const { url } = await getDrawingPreviewUrl(jobId, drawingId);
    window.open(url, "_blank", "noopener");
  } catch (e) {
    window.alert(`Could not open drawing: ${e instanceof Error ? e.message : String(e)}`);
    onFallback?.();
  }
}
